import React, { useMemo } from 'react';
import cn from 'classnames';
import Carousel from 'react-multi-carousel';
import { RecoilRoot } from 'recoil';
import { PortalProvider } from 'react-portal-hook';
import { VehicleModel } from './components/model-details';
import { SvgIcon } from '~/common/components/ui-elements';
import { ModelEntry } from './types';
import ModelRangeTab from './components/model-range-tab';
import styles from './index.module.scss';

interface SubaruModelRangeProps {
  contentId: number;
  modelEntries: ModelEntry[];
}

const SubaruModelRange = React.memo<SubaruModelRangeProps>(
  ({ modelEntries }) => {
    const carouselRef = React.useRef<Carousel>(null);
    const [activeEntries, setActiveEntries] = React.useState<ModelEntry[]>([]);

    const onCategorySelect = (category: string) => {
      carouselRef.current?.goToSlide(0);
      setActiveEntries(
        modelEntries.filter((x) => x.categoryTitle === category)
      );
    };

    const itemCountDesktop = Math.min(activeEntries.length, 3);
    const itemCountTablet = Math.min(activeEntries.length, 2);

    const responsive = useMemo(
      () => ({
        desktop: {
          breakpoint: { max: Number.MAX_SAFE_INTEGER, min: 1300 },
          items: itemCountDesktop,
          slidesToSlide: 3,
        },
        tablet: {
          breakpoint: { max: 1300, min: 464 },
          items: itemCountTablet,
          slidesToSlide: 2,
        },
        mobile: {
          breakpoint: { max: 464, min: 0 },
          items: 1,
          slidesToSlide: 1,
        },
      }),
      [itemCountDesktop, itemCountTablet]
    );

    return (
      <RecoilRoot>
        <PortalProvider>
          <div className={styles.subaruModelRange}>
            <ModelRangeTab
              modelEntries={modelEntries}
              onSelect={onCategorySelect}
            />

            <div
              className={cn(
                styles.showDesktopCarousel,
                activeEntries.length <= 2 && styles.centerContent
              )}
            >
              <Carousel
                key={activeEntries.length}
                ref={carouselRef}
                arrows
                swipeable
                centerMode={false}
                customLeftArrow={<CustomLeftArrow />}
                customRightArrow={<CustomRightArrow />}
                containerClass={cn(
                  styles.carouselContainer,
                  activeEntries.length === 1 && styles.carouselContainerCenter,
                  activeEntries.length >= 3 && styles.containerSizeThree,
                  activeEntries.length === 2 && styles.containerSizeTwo,
                  activeEntries.length === 1 && styles.containerSizeOne
                )}
                responsive={responsive}
              >
                {activeEntries.map((modelEntry, index) => (
                  <VehicleModel
                    key={index}
                    model={modelEntry}
                    className={styles.carouselItemModel}
                  />
                ))}
              </Carousel>
            </div>
          </div>
        </PortalProvider>
      </RecoilRoot>
    );
  }
);

export default SubaruModelRange;

interface ArrowsProps {
  onClick?: () => void;
}
const CustomLeftArrow = ({ onClick }: ArrowsProps) => {
  return (
    <SvgIcon
      className={styles.customLeftArrow}
      type="chevronLeft"
      size={2}
      onClick={onClick}
    />
  );
};

const CustomRightArrow = ({ onClick }: ArrowsProps) => {
  return (
    <SvgIcon
      className={styles.customRightArrow}
      type="chevronRight"
      size={2}
      onClick={onClick}
    />
  );
};
